/**
 * `pending-ai-thumbs-feedback` v0 — wire contracts.
 *
 * One row = one user/operator vote on a single AI surface.
 * Surfaces are listed in `AiContentKindSchema`; new surfaces add a
 * value here AND a check-constraint variant in mig 144.
 */
import { z } from 'zod';

export const AiContentKindSchema = z.enum([
  'flake-hypothesis',
  'release-summary',
  'intent-suggestion',
  'element-hint',
  'exploratory-finding',
  'flow-template-summ',
  'other',
]);
export type AiContentKind = z.infer<typeof AiContentKindSchema>;

export const AiFeedbackScoreSchema = z.enum(['up', 'down']);
export type AiFeedbackScore = z.infer<typeof AiFeedbackScoreSchema>;

export const AiFeedbackEntrySchema = z.object({
  id: z.string().uuid(),
  contentKind: AiContentKindSchema,
  surfaceId: z.string().min(1).max(200),
  score: AiFeedbackScoreSchema,
  comment: z.string().max(1000).nullable(),
  userId: z.string().uuid().nullable(),
  orgId: z.string().uuid().nullable(),
  provider: z.string().max(64).nullable(),
  model: z.string().max(128).nullable(),
  meta: z.record(z.unknown()).default({}),
  createdAt: z.string(),
});
export type AiFeedbackEntry = z.infer<typeof AiFeedbackEntrySchema>;

export const SubmitAiFeedbackRequestSchema = z.object({
  contentKind: AiContentKindSchema,
  surfaceId: z.string().min(1).max(200),
  score: AiFeedbackScoreSchema,
  comment: z.string().max(1000).optional(),
  provider: z.string().max(64).optional(),
  model: z.string().max(128).optional(),
  meta: z.record(z.unknown()).optional(),
});
export type SubmitAiFeedbackRequest = z.infer<typeof SubmitAiFeedbackRequestSchema>;

export const AiFeedbackTriageRowSchema = AiFeedbackEntrySchema.extend({
  // Pivot: per-(kind, score) totals over the same window the panel
  // is showing. Kept on the row for cheap rendering.
  totalsForKind: z
    .object({
      up: z.number().int().nonnegative(),
      down: z.number().int().nonnegative(),
    })
    .optional(),
});
export type AiFeedbackTriageRow = z.infer<typeof AiFeedbackTriageRowSchema>;

export const AiFeedbackSummarySchema = z.object({
  contentKind: AiContentKindSchema,
  up: z.number().int().nonnegative(),
  down: z.number().int().nonnegative(),
  satisfaction: z.number().min(0).max(1),
});
export type AiFeedbackSummary = z.infer<typeof AiFeedbackSummarySchema>;

export const ListAiFeedbackResponseSchema = z.object({
  count: z.number().int().nonnegative(),
  entries: z.array(AiFeedbackEntrySchema),
  summary: z.array(AiFeedbackSummarySchema),
});
export type ListAiFeedbackResponse = z.infer<typeof ListAiFeedbackResponseSchema>;
